import { Button, Flex, Icon, Text, Tooltip } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaMagic } from "react-icons/fa";
import { type NeedDto, type NeedPatchDto } from "../../autogen/bff-api";
import { useEditor } from "../../common/editing/save-coordinator";
import { tiptapToPlainText } from "../../common/input/TipTap/tiptapToPlainText";
import { useNeedTypeSuggestion } from "./useNeedTypeSuggestion";

export const NeedTypeAiSuggestion = ({ needId }: { needId: string }) => {
  const { t } = useTranslation();
  const { state, updateField } = useEditor<NeedDto, NeedPatchDto>(needId);
  const typeSuggestion = useNeedTypeSuggestion();
  const [reasoning, setReasoning] = useState<string | null>(null);

  const title = state.title?.trim() ?? "";
  const description = useMemo(() => tiptapToPlainText(state.description), [state.description]);
  const hasContext = title !== "" || description !== "";

  const handleSuggest = async () => {
    setReasoning(null);
    const suggestion = await typeSuggestion.suggest(title, description);
    if (!suggestion) return;
    updateField({ procurementType: suggestion.procurementType });
    if (!state.priority) updateField({ priority: suggestion.priority });
    setReasoning(suggestion.reasoning || null);
  };

  return (
    <Flex direction="column" align="flex-start" gap="2" mt="3">
      <Tooltip label={t("Add a title or description first")} isDisabled={hasContext} shouldWrapChildren>
        <Button
          size="xs"
          variant="outline"
          colorScheme="teal"
          leftIcon={<Icon as={FaMagic} />}
          onClick={handleSuggest}
          isLoading={typeSuggestion.isLoading}
          loadingText={t("Analyzing…")}
          isDisabled={!hasContext}
        >
          {t("Suggest type & priority with AI")}
        </Button>
      </Tooltip>
      {reasoning && (
        <Text fontSize="xs" color="smMuted">
          {reasoning}
        </Text>
      )}
      {typeSuggestion.hasError && (
        <Text fontSize="xs" color="smError">
          {t("Couldn't generate suggestions. Please try again.")}
        </Text>
      )}
    </Flex>
  );
};
